/**
 * useRefetchFromShopify — Sub-PR I (issue #516)
 *
 * Thin TanStack Query mutation wrapper around
 * `POST /api/orders/:shopifyId/refetch`. On any of the three
 * generated success kinds. Only an `applied` result mutates local state,
 * so only that outcome invalidates the order query namespace.
 *
 * Error surfacing is intentionally minimal: the component decides how
 * to render the message (toast in OrderActionsToolbar) — the hook just
 * shapes the request + invalidation.
 *
 * The backend enforces `requireAuth + requireAdmin`. The component
 * gates the trigger on `isAdmin` so non-admin operators don't see a
 * button that always 403s.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { http } from '@/lib/api';
import { queryKeys } from '@/lib/queryKeys';
import { REFETCH_RESULT_KINDS, type RefetchResult } from '@/services/orders';

const SHOULD_INVALIDATE_BY_KIND = {
  [REFETCH_RESULT_KINDS.APPLIED]: true,
  [REFETCH_RESULT_KINDS.STALE_EVENT]: false,
  [REFETCH_RESULT_KINDS.NOT_FOUND_IN_SHOPIFY]: false,
} as const satisfies Record<RefetchResult['kind'], boolean>;

/**
 * `shopifyOrderId: string` (not `string | undefined`) — `Order.shopify_id`
 * is non-optional on the resource model (`src/services/orders.ts::Order`),
 * so every call site already passes a concrete string. Tightening the
 * type lets the compiler — rather than a runtime `throw` — catch any
 * future caller that loses the id.
 */
export function useRefetchFromShopify(shopifyOrderId: string) {
  const queryClient = useQueryClient();

  return useMutation<RefetchResult, Error, void>({
    mutationFn: async () => {
      return http.post<RefetchResult>(`/api/orders/${shopifyOrderId}/refetch`);
    },
    onSuccess: (data) => {
      // Only invalidate when state actually changed. `stale_event`
      // and `not_found_in_shopify` carry no DB-side mutation; skipping
      // the invalidate avoids a useless re-render flicker.
      //
      // R2 fix: invalidate ONLY `orders.all()`. The earlier R1 fix
      // added `tasks.all()` thinking the per-order panel mounted a
      // separate tasks query; in fact `OrderDetailExtra` reads
      // `order.tasks` as a denormalised array embedded in the
      // enriched `getOrderById` response, which `orders.detail(id)`
      // already refreshes. Invalidating `tasks.all()` here was a
      // no-op for this panel — it only refreshed the standalone
      // driver-task views, which the operator is not looking at after
      // a refetch click and which #2081 deleted outright.
      if (SHOULD_INVALIDATE_BY_KIND[data.kind]) {
        queryClient.invalidateQueries({ queryKey: queryKeys.orders.all() });
      }
    },
  });
}
